import { useState, useEffect } from "react"
import { Link } from "react-router-dom"
import Input from "~/ui-rtk/components/ui/controls/Input"
import Select from "~/ui-rtk/components/ui/controls/Select"
import { cn } from "~/ui-rtk/utils/tailwind-utils"
import { formatNumber } from "~/ui-rtk/utils/format"
import { DAILY_BUDGET_EVAL_PERIOD_OPTIONS } from "~/ui-rtk/constants/daily-budget-eval"
import { ANALYSIS_PERIOD_SELECT_OPTIONS, type BudgetInputs } from "../types"

interface LockState {
  targetDRRoasLocked: boolean
  targetBrandRoasLocked: boolean
  targetDailyBudgetLocked: boolean
}

interface InputPanelProps {
  inputs: BudgetInputs
  onChange: (inputs: BudgetInputs) => void
  isLoading?: boolean
  settingsLink?: string
  lockState?: LockState
  globalAnalysisPeriod: number
  onGlobalAnalysisPeriodChange: (days: number) => void
  globalAnalysisPeriodDisabled?: boolean
  dailyBudgetEvalPeriod: number
  onDailyBudgetEvalPeriodChange: (days: number) => void
  recommendedDailyBudget?: number | null
  /** Set every row's analysis period to its shortest High-confidence window */
  onAutoSelectAnalysisPeriods?: () => void
}

export default function InputPanel({
  inputs,
  onChange,
  isLoading,
  settingsLink,
  lockState,
  globalAnalysisPeriod,
  onGlobalAnalysisPeriodChange,
  globalAnalysisPeriodDisabled,
  dailyBudgetEvalPeriod,
  onDailyBudgetEvalPeriodChange,
  recommendedDailyBudget,
  onAutoSelectAnalysisPeriods,
}: InputPanelProps) {
  // Track raw string values so the user can freely clear and retype
  const [rawValues, setRawValues] = useState<
    Record<keyof BudgetInputs, string>
  >({
    targetDRRoas: String(inputs.targetDRRoas),
    targetBrandRoas: String(inputs.targetBrandRoas),
    targetDailyBudget: String(inputs.targetDailyBudget),
    avgOrderValue: String(inputs.avgOrderValue),
  })

  // Sync rawValues when inputs change externally (e.g. from Financial Settings loading)
  useEffect(() => {
    setRawValues(prev => {
      const next = { ...prev }
      for (const key of Object.keys(inputs) as (keyof BudgetInputs)[]) {
        const currentParsed = parseFloat(prev[key])
        if (isNaN(currentParsed) || currentParsed !== inputs[key]) {
          next[key] = inputs[key] ? String(inputs[key]) : ""
        }
      }
      return next
    })
  }, [inputs])

  const handleChange = (field: keyof BudgetInputs, value: string) => {
    // Skip changes for locked fields
    const lockMap: Partial<Record<keyof BudgetInputs, keyof LockState>> = {
      targetDRRoas: "targetDRRoasLocked",
      targetBrandRoas: "targetBrandRoasLocked",
      targetDailyBudget: "targetDailyBudgetLocked",
    }
    const lockKey = lockMap[field]
    if (lockKey && lockState?.[lockKey]) return

    setRawValues(prev => ({ ...prev, [field]: value }))
    const num = parseFloat(value)
    if (!isNaN(num) && num >= 0) {
      onChange({ ...inputs, [field]: num })
    }
  }

  // Auto-select works off confidence, which needs an AOV to estimate conversions
  const aovSet = inputs.avgOrderValue > 0
  const canAutoSelect = aovSet && !globalAnalysisPeriodDisabled
  const autoSelectTitle = aovSet
    ? "Set every row to the shortest analysis period with High confidence. Rows that never reach High use the period with the most estimated conversions."
    : "Set Avg Order Value to estimate confidence"

  return (
    <div className="bg-system-blue-800 rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-white font-medium text-sm">
          Budget Allocation Targets
        </h3>
        {settingsLink && (
          <Link
            to={settingsLink}
            className="text-xs text-system-brand-pink-400 hover:text-system-brand-pink-300 underline transition-colors"
          >
            Edit defaults in Financial Settings
          </Link>
        )}
      </div>
      {isLoading ? (
        <div className="grid grid-cols-5 gap-6">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="space-y-1">
              <div className="h-4 w-24 bg-system-blue-600/40 rounded animate-pulse" />
              <div className="h-10 bg-system-blue-600/40 rounded animate-pulse" />
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-5 gap-6">
          <div>
            <label className="text-gray-400 text-xs block mb-1">
              Target DR ROAS
            </label>
            <Input
              type="number"
              value={rawValues.targetDRRoas}
              onChange={e => handleChange("targetDRRoas", e.target.value)}
              step="0.1"
              min="0"
              readOnly={lockState?.targetDRRoasLocked}
              className={cn(
                "w-full",
                lockState?.targetDRRoasLocked && "opacity-60",
              )}
            />
            {lockState?.targetDRRoasLocked && settingsLink && (
              <p className="text-xs text-system-gray-500 mt-1">
                Locked to recommended.{" "}
                <Link
                  to={settingsLink}
                  className="text-system-brand-pink-400 hover:text-system-brand-pink-300 underline"
                >
                  Update in Financial Settings
                </Link>
              </p>
            )}
          </div>
          <div>
            <label className="text-gray-400 text-xs block mb-1">
              Target Brand ROAS
            </label>
            <Input
              type="number"
              value={rawValues.targetBrandRoas}
              onChange={e => handleChange("targetBrandRoas", e.target.value)}
              step="0.1"
              min="0"
              readOnly={lockState?.targetBrandRoasLocked}
              className={cn(
                "w-full",
                lockState?.targetBrandRoasLocked && "opacity-60",
              )}
            />
            {lockState?.targetBrandRoasLocked && settingsLink && (
              <p className="text-xs text-system-gray-500 mt-1">
                Locked to recommended.{" "}
                <Link
                  to={settingsLink}
                  className="text-system-brand-pink-400 hover:text-system-brand-pink-300 underline"
                >
                  Update in Financial Settings
                </Link>
              </p>
            )}
          </div>
          <div>
            <label className="text-gray-400 text-xs block mb-1">
              Target Daily Budget ($)
            </label>
            <Input
              type="number"
              value={rawValues.targetDailyBudget}
              onChange={e => handleChange("targetDailyBudget", e.target.value)}
              step="100"
              min="0"
              readOnly={lockState?.targetDailyBudgetLocked}
              className={cn(
                "w-full",
                lockState?.targetDailyBudgetLocked && "opacity-60",
              )}
            />
            <Select
              aria-label="Daily budget evaluation period"
              options={DAILY_BUDGET_EVAL_PERIOD_OPTIONS}
              value={dailyBudgetEvalPeriod}
              onChange={e =>
                onDailyBudgetEvalPeriodChange(Number(e.target.value))
              }
              classes={{
                select: "mt-1 min-h-0 h-[24px] text-xs rounded-lg",
              }}
            />
            {!lockState?.targetDailyBudgetLocked &&
              recommendedDailyBudget != null &&
              recommendedDailyBudget > 0 &&
              recommendedDailyBudget !== inputs.targetDailyBudget && (
                <p className="text-xs text-system-gray-500 mt-1">
                  Recommended: ${formatNumber(recommendedDailyBudget)}{" "}
                  <button
                    type="button"
                    className="text-system-brand-pink-400 hover:text-system-brand-pink-300 underline"
                    onClick={() =>
                      handleChange(
                        "targetDailyBudget",
                        String(recommendedDailyBudget),
                      )
                    }
                  >
                    Use recommended
                  </button>
                </p>
              )}
            {lockState?.targetDailyBudgetLocked && settingsLink && (
              <p className="text-xs text-system-gray-500 mt-1">
                Locked to recommended.{" "}
                <Link
                  to={settingsLink}
                  className="text-system-brand-pink-400 hover:text-system-brand-pink-300 underline"
                >
                  Update in Financial Settings
                </Link>
              </p>
            )}
          </div>
          <div>
            <label className="text-gray-400 text-xs block mb-1">
              Avg Order Value ($)
            </label>
            <Input
              type="number"
              value={rawValues.avgOrderValue}
              onChange={e => handleChange("avgOrderValue", e.target.value)}
              step="1"
              min="0"
              className="w-full"
            />
            <p className="text-xs text-system-gray-500 mt-1">
              Used to estimate conversions for confidence.
            </p>
          </div>
          <div>
            <label className="text-gray-400 text-xs block mb-1">
              Analysis Period (Global)
            </label>
            <Select
              aria-label="Global analysis period"
              options={ANALYSIS_PERIOD_SELECT_OPTIONS}
              value={globalAnalysisPeriod}
              disabled={globalAnalysisPeriodDisabled}
              onChange={e =>
                onGlobalAnalysisPeriodChange(Number(e.target.value))
              }
              classes={{
                // Match the Input controls' height (~30px) instead of the
                // DaisyUI default h-12, which towers over the inputs
                select: "min-h-0 h-[30px] text-sm font-semibold rounded-lg",
              }}
            />
            <p className="text-xs text-system-gray-500 mt-1">
              Applies to all rows; rows can still be changed individually.
            </p>
            {onAutoSelectAnalysisPeriods && (
              <p className="text-xs text-system-gray-500 mt-1">
                <button
                  type="button"
                  disabled={!canAutoSelect}
                  title={autoSelectTitle}
                  className="text-system-brand-pink-400 hover:text-system-brand-pink-300 underline disabled:opacity-50 disabled:cursor-not-allowed disabled:no-underline"
                  onClick={onAutoSelectAnalysisPeriods}
                >
                  Auto-select by confidence
                </button>
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
